import React, { useState, useEffect, useRef } from "react";
import { useApp } from "@/context/AppContext";
import { MessageCircle, X } from "lucide-react";
import { getWhatsappNumbers } from "@/lib/whatsappLink";
import api from "@/lib/api";

function normalizeDigits(n) {
  if (!n) return "";
  return String(n).replace(/[^\d]/g, "");
}

function formatDisplay(n) {
  // Preserve owner-supplied "+91 X" formatting; strip only surrounding whitespace.
  return String(n || "").trim();
}

export default function WhatsAppFab({ message }) {
  const { settings, t, customerToken, customerInfo, cart, extras, selectedPackageId, eventInfo } = useApp();
  const numbers = getWhatsappNumbers(settings);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  // Context-aware message: when the visitor has a package/menu selection and a
  // guest count, mirror the sidebar "Send on WhatsApp" note (package, guests,
  // estimated subtotal) so the owner always sees the calculated amount.
  const guests = Number(eventInfo?.guests) || 0;
  const hasSelection = !!selectedPackageId || cart.length > 0 || extras.length > 0;
  const [pkg, setPkg] = useState(null);
  const [estimate, setEstimate] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        if (selectedPackageId) {
          const { data } = await api.get("/packages");
          setPkg((data || []).find((p) => p.id === selectedPackageId) || null);
        } else setPkg(null);
        if (hasSelection) {
          const { data } = await api.post("/pricing/estimate", {
            package_id: selectedPackageId || null,
            guests: guests || 0,
            dish_ids: cart.map((c) => c.id),
            extras: extras.map((e) => ({
              dish_id: e.dish_id && !String(e.dish_id).startsWith("cat:") ? e.dish_id : null,
              category_id: e.category_id,
              qty: e.qty,
            })),
          });
          setEstimate(data);
        } else setEstimate(null);
      } catch (e) { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, extras, selectedPackageId, guests, hasSelection]);

  const detailLines =
    (eventInfo?.type ? `Event: ${eventInfo.type}\n` : "") +
    (guests ? `Guests: ${guests}\n` : "") +
    (pkg ? `Package: ${pkg.name}\n` : "") +
    (cart.length ? `Dishes: ${cart.map((c) => c.name).join(", ")}\n` : "") +
    (extras.length ? `Extras: ${extras.map((e) => `${e.dish_name} (${e.category_name}) ×${e.qty}`).join(", ")}\n` : "") +
    (estimate?.subtotal != null ? `Estimated subtotal: ₹${Number(estimate.subtotal).toLocaleString("en-IN")}\n` : "");
  const contextMessage = detailLines
    ? `Hello Tezz Khana! I would like to enquire about catering.\n${detailLines}\n(Sent from the Tezz Khana website)`
    : null;

  // Silently save an enquiry to Admin → Enquiries when the customer starts a
  // WhatsApp chat (same pattern as the "Send on WhatsApp" quote button).
  // Deduped: one enquiry per visit, even across repeated clicks.
  const savedRef = useRef(false);
  const saveEnquiry = (waNumber) => {
    if (savedRef.current) return;
    savedRef.current = true;
    api.post("/enquiries", {
      customer_name: (customerToken && customerInfo?.name) || "WhatsApp enquiry (name pending)",
      mobile: (customerToken && customerInfo?.mobile) || "via WhatsApp",
      email: (customerToken && customerInfo?.email) || null,
      event_type: eventInfo?.type || null,
      guest_count: guests || null,
      estimated_amount: estimate?.subtotal ?? estimate?.subtotal_partial ?? null,
      selected_menu: [
        ...(pkg ? [{ kind: "package", id: pkg.id, name: pkg.name, tier: pkg.tier }] : []),
        ...cart.map((c) => ({ kind: "dish", id: c.id, name: c.name, qty: c.qty })),
        ...extras.map((e) => ({ kind: "extra", dish_id: e.dish_id, category_id: e.category_id, name: e.dish_name, category: e.category_name, qty: e.qty })),
        ...(estimate ? [{ kind: "pricing", subtotal: estimate.subtotal, subtotal_partial: estimate.subtotal_partial, needs_config: estimate.needs_config }] : []),
      ],
      special_requirements: `Clicked the WhatsApp chat button (to ${waNumber}) on ${window.location.pathname} — customer is messaging on WhatsApp.`,
    }).catch(() => {
      savedRef.current = false;
    });
  };

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (numbers.length === 0) return null;

  const text = encodeURIComponent(
    message || contextMessage || `Hello! I would like to enquire about catering with ${settings?.business_name || "Tezz Khana"}.`
  );

  // Single number: keep exact original behaviour — direct link.
  if (numbers.length === 1) {
    const digits = normalizeDigits(numbers[0]);
    if (!digits) return null;
    return (
      <a
        href={`https://wa.me/${digits}?text=${text}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => saveEnquiry(numbers[0])}
        data-testid="whatsapp-fab"
        aria-label={t("nav.contact")}
        className="fixed bottom-6 right-6 z-40 inline-flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#25D366] text-sm font-semibold text-white shadow-lg shadow-black/10 transition-transform hover:-translate-y-0.5 sm:h-auto sm:w-auto sm:gap-2 sm:px-5 sm:py-3"
      >
        <MessageCircle className="h-5 w-5 sm:h-4 sm:w-4" aria-hidden />
        <span className="hidden sm:inline">WhatsApp</span>
      </a>
    );
  }

  // 2+ numbers: same green FAB, but tapping opens a small choice popover.
  return (
    <div ref={popRef} className="fixed bottom-6 right-6 z-40">
      {open && (
        <div
          data-testid="whatsapp-fab-popover"
          className="mb-3 min-w-[240px] rounded-2xl border border-border/60 bg-card p-3 shadow-xl shadow-black/15"
          role="menu"
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Choose WhatsApp
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="grid h-6 w-6 place-items-center rounded-full hover:bg-muted"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <ul className="flex flex-col gap-1.5">
            {numbers.map((n, idx) => {
              const digits = normalizeDigits(n);
              if (!digits) return null;
              return (
                <li key={digits + idx}>
                  <a
                    href={`https://wa.me/${digits}?text=${text}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`whatsapp-fab-option-${idx + 1}`}
                    onClick={() => {
                      saveEnquiry(n);
                      setOpen(false);
                    }}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2 text-sm font-medium text-secondary hover:bg-[#25D366]/10 hover:border-[#25D366]/40"
                  >
                    <span>WhatsApp {idx + 1}</span>
                    <span className="text-xs text-muted-foreground">{formatDisplay(n)}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="whatsapp-fab"
        aria-label={t("nav.contact")}
        className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#25D366] text-sm font-semibold text-white shadow-lg shadow-black/10 transition-transform hover:-translate-y-0.5 sm:h-auto sm:w-auto sm:gap-2 sm:px-5 sm:py-3"
      >
        <MessageCircle className="h-5 w-5 sm:h-4 sm:w-4" aria-hidden />
        <span className="hidden sm:inline">WhatsApp</span>
      </button>
    </div>
  );
}
